// @flow

import * as Sentry from '@sentry/react';
import config from 'config/app.config';
import { getHomeUrl } from 'config/paths';
import { ActionsObservable, combineEpics } from 'redux-observable';
import { of } from 'rxjs';
import { ajax } from 'rxjs/ajax';
import { catchError, debounceTime, filter, map, switchMap } from 'rxjs/operators';

import { API } from '../../utils/APIUtils';
import { getCookie } from '../../utils/CommonUtils';
import {
  GET_SESSION_ID,
  GET_SESSION_ID_DONE,
  GET_SESSION_ID_RETRY,
  SAVE_JWT,
  SET_SESSION_ID,
  WS_CLEAR_PENDING,
  WS_DISPLAY_ERROR_LAYER,
  WS_INIT_REGISTRATION_SUCCESS,
  WS_SET_SESSION_ID_ERROR,
  WS_SET_SESSION_ID_REQUEST,
  WS_SET_SESSION_ID_SUCCESS,
} from '../actions';
import type {
  SessionActions,
  SetSessionIdParams,
  SetSessionIdResponse,
  SetSessionIdError,
} from '../types';

import { afterInitRegistration } from './userRegistrationEpics';
import { sendMessage } from './websocketEpics';

export const onInitRegistrationSuccess = (action$: ActionsObservable<SessionActions>) =>
  action$.pipe(
    filter(action => action.type === WS_INIT_REGISTRATION_SUCCESS),
    switchMap(() => {
      const dispatchActions = [];

      const jwt = getCookie(config.nameCookieJWT);
      if (jwt) {
        dispatchActions.push({
          type: SAVE_JWT,
          payload: jwt,
        });

        dispatchActions.push({ type: SET_SESSION_ID });
      }

      return of(...dispatchActions);
    }),
  );

export const getSessionIdEpic = (action$: ActionsObservable<SessionActions>) =>
  action$.pipe(
    filter(action => action.type === GET_SESSION_ID),
    switchMap(request =>
      ajax({
        url: config.session.getURL,
        withCredentials: true,
      }).pipe(
        map((ajaxResponse) => {
          const sessionId = config.isProd
            ? ajaxResponse.xhr.getResponseHeader(config.session.headerName)
            : config.session.devSessionId;
          return {
            type: GET_SESSION_ID_DONE,
            payload: { sessionId },
            retry: request.retry || 0,
            dispatchOnSuccess: request.dispatchOnSuccess,
            message: `${request.message ?? ''}\n/get_session_id_epic[success]`,
          };
        }),
        catchError(error =>
          of({
            type: GET_SESSION_ID_DONE,
            payload: { error },
            retry: request.retry || 0,
            dispatchOnSuccess: request.dispatchOnSuccess,
            message: `${request.message ?? ''}\n/get_session_id_epic[error]`,
          })),
      )),
  );

export const getSessionIdDoneEpic = (action$: ActionsObservable<SessionActions>) =>
  action$.pipe(
    filter(action => action.type === GET_SESSION_ID_DONE),
    switchMap((request) => {
      const { sessionId, error } = request.payload;
      const { retry, dispatchOnSuccess } = request;
      if (error) {
        return of({
          type: GET_SESSION_ID_RETRY,
          retry,
          dispatchOnSuccess,
          message: `${request.message ?? ''}\n/get_session_id_done_epic[error]`,
        });
      }

      const dispatchActions = [];

      const jwt = getCookie(config.nameCookieJWT);
      if (jwt) {
        dispatchActions.push({
          type: SAVE_JWT,
          payload: jwt,
          message: `${request.message ?? ''}\n/get_session_id_done_epic[jwt]`,
        });
      }

      if (jwt && sessionId) {
        dispatchActions.push({
          type: WS_SET_SESSION_ID_REQUEST,
          payload: { params: { session_id: sessionId, jwt } },
          retry,
          dispatchOnSuccess,
          message: `${request.message ?? ''}\n/get_session_id_done_epic[jwtAndSessionId]`,
        });
      } else {
        dispatchActions.push({
          type: GET_SESSION_ID_RETRY,
          retry,
          dispatchOnSuccess,
          message: `${request.message ?? ''}\n/get_session_id_done_epic[noJwtOrSessionId]`,
        });
      }

      return of(...dispatchActions);
    }),
  );

export const getSessionIdRetryEpic = (action$: ActionsObservable<SessionActions>) =>
  action$.pipe(
    filter(action => action.type === GET_SESSION_ID_RETRY),
    debounceTime(config.session.debounceRetry),
    switchMap((request) => {
      const { retry, dispatchOnSuccess } = request;

      if (retry < config.session.maxRetry) {
        return of({
          type: GET_SESSION_ID,
          retry: retry + 1,
          dispatchOnSuccess,
          message: `${request.message ?? ''}\n/get_session_id_retry_epic[retry]`,
        });
      }

      return of({
        type: WS_DISPLAY_ERROR_LAYER,
        payload: {
          message: `${(config.license === 'PT') ? request.message : 'It seems there is a connection problem'}`,
          buttons: [
            {
              picto: 'arrow-right',
              title: 'Try again',
              onClick: () => {
                window.location.reload();
              },
            },
            {
              theme: 'secondary',
              picto: 'close',
              // __('Leave')
              title: 'Leave',
              onClick: () => {
                window.location.href = getHomeUrl();
              },
            },
          ],
        },
      });
    }),
  );

export const setSessionIdRequestEpic = (action$: ActionsObservable<SessionActions>, state$: any) =>
  action$.pipe(
    filter(action => action.type === WS_SET_SESSION_ID_REQUEST),
    afterInitRegistration(state$, (actionSend: SessionActions) => {
      if (actionSend.type === WS_SET_SESSION_ID_REQUEST) {
        return sendMessage<SetSessionIdParams, SetSessionIdResponse, SetSessionIdError>({
          ...actionSend.payload,
          method: API.SetSessionId,
        }).pipe(
          switchMap((payload) => {
            // $FlowFixMe
            const { retry, dispatchOnSuccess } = actionSend;
            if (payload.error) {
              const dispatchActions = [
                {
                  type: WS_SET_SESSION_ID_ERROR,
                  payload: payload.error,
                },
                { type: WS_CLEAR_PENDING },
              ];

              if (
                payload.error.code
                && (payload.error.code === -32602 || payload.error.code === -32603)
              ) {
                dispatchActions.push({
                  type: GET_SESSION_ID_RETRY,
                  retry,
                  dispatchOnSuccess,
                });
              } else {
                dispatchActions.push({
                  type: WS_DISPLAY_ERROR_LAYER,
                  payload: payload.error,
                });
              }

              return of(...dispatchActions);
            }

            const sessionId = actionSend.payload.params.session_id;
            if (sessionId) {
              Sentry.setTag('sessionId', sessionId);
            }

            const dispatchActions = [{ type: WS_SET_SESSION_ID_SUCCESS, payload: payload.result }];

            if (dispatchOnSuccess) {
              dispatchActions.push(...dispatchOnSuccess);
            } else {
              dispatchActions.push({ type: WS_CLEAR_PENDING });
            }

            return of(...dispatchActions);
          }),
        );
      }
      return null;
    }),
  );

export const sessionEpics = combineEpics(
  onInitRegistrationSuccess,
  getSessionIdEpic,
  getSessionIdDoneEpic,
  getSessionIdRetryEpic,
  setSessionIdRequestEpic,
);
